import React from "react";
import { Controller, Control, RegisterOptions, useFormContext } from "react-hook-form";
import { FormControl, FormHelperText } from "@material-ui/core";
import styled from "styled-components";
import PhoneInput from "react-phone-number-input";
import FontStyles from "../../styles/fontStyles";
import { Spacing } from "../../../config";

const Label = styled.p(({ theme, $withError }: any) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.semibold14,
    textTransform: "capitalize",
    padding: 0,
    margin: 0,
    color: $withError ? colors.error.main : "#0C396D",
    transition: ".2s",
    pointerEvents: "none",
    alignItems: "center",
    display: "flex",
    overflow: "hidden",
    fontSize: "12px",
  };
});

const LabelView = styled.div(() => ({
  display: "flex",
  alignContent: "center",
  justifyContent: "flex-start",
}));

const StyledInput = styled(PhoneInput)(({ theme, error, $inputStyle }: any) => ({
  ...FontStyles.medium14,
  padding: theme.spacing(1.962, 2.5),
  marginTop: Spacing(1.5),
  textOverflow: "ellipsis",
  color: error ? "red" : theme.palette.text.primary,
  backgroundColor: theme.palette.primary.contrastText,
  borderBottom: "0px",
  borderRadius: 21,
  "& .PhoneInputCountry": {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  "& .PhoneInputCountrySelect": {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    zIndex: 1,
    border: 0,
    opacity: 0,
    cursor: "pointer",
  },
  "& .PhoneInputCountryIcon": {
    width: "28px",
    display: "flex",
  },
  "& .PhoneInputCountrySelectArrow": {
    marginRight: "8px",
    marginLeft: "8px",
    marginTop: "-4px",
    display: "block",
    content: "''",
    width: "11px",
    height: "11px",
    borderStyle: "solid",
    borderColor: "var(--PhoneInputCountrySelectArrow-color, currentColor)",
    borderTopWidth: 0,
    borderBottomWidth: "var(--PhoneInputCountrySelectArrow-borderWidth, 1px)",
    borderLeftWidth: 0,
    borderRightWidth: "var(--PhoneInputCountrySelectArrow-borderWidth, 1px)",
    transform: "var(--PhoneInputCountrySelectArrow-transform, rotate(45deg))",
    opacity: "var(--PhoneInputCountrySelectArrow-opacity, 0.45)",
  },
  "& .PhoneInputInput": {
    ...FontStyles.medium14,
    border: "none",
    textOverflow: "ellipsis",
    color: error ? "red" : "black",
    backgroundColor: "transparent",
    display: "flex",
    width: "100%",
    "&:focus-visible": { outline: "none" },
  },
  "& .PhoneInputInput:focus-visible": { outline: "none" },
  ...$inputStyle,
}));

const ErrorMessage = styled(FormHelperText)(() => ({
  color: "red",
  minHeight: Spacing(2.5),
}));

export interface InputPhoneRHFProps {
  control: Control<any>;
  name: string;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  rules?: RegisterOptions;
  isBrasil?: boolean;
  placeholder?: string;
}

function InputPhoneRHF({
  control,
  name,
  label,
  disabled,
  readOnly,
  rules,
  isBrasil = true,
  placeholder,
}: InputPhoneRHFProps) {
  const formContext = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;
        const normalizePhone = (val: string) => {
          if (!val) return "";
          if (val.startsWith("+")) return val;
          if (/^\d{10,11}$/.test(val)) return `+55${val}`;
          return val;
        };
        return (
          <FormControl fullWidth error={Boolean(errorMsg)}>
            {label && (
              <LabelView>
                <Label $withError={Boolean(errorMsg)}>{label}</Label>
              </LabelView>
            )}
            <StyledInput
              defaultCountry={isBrasil ? "BR" : undefined}
              international={true}
              disabled={disabled}
              readOnly={readOnly}
              error={Boolean(errorMsg)}
              name={name}
              value={normalizePhone(field.value ?? "")}
              placeholder={placeholder}
              onChange={(value: any) => {
                field.onChange(value ?? "");
                if (fieldState.invalid) formContext?.trigger(name);
              }}
              onBlur={() => {
                field.onBlur();
                formContext?.trigger(name);
              }}
            />
            <ErrorMessage error={Boolean(errorMsg)}>{errorMsg}</ErrorMessage>
          </FormControl>
        );
      }}
    />
  );
}

export default InputPhoneRHF;
